'use client'
import React, { useEffect, useState } from 'react'
import Container from '@/components/shared/Container'
import { useCartStore } from '@/core/hooks/cart/useCartStore'
import { useDictionary } from '@/core/hooks/use-dictionary'
import { usePathname } from 'next/navigation'
import { OrderSummary } from '@/components/main/cart/OrderSummary'
import CartStepper from '@/components/main/cart/CartStepper'
import CartSkeleton from '@/components/main/cart/CartSkeleton'
import { Heading } from '@/components/ui/Heading'

interface CheckoutLayoutProps {
    children: React.ReactNode
}

export default function CartLayout({ children }: CheckoutLayoutProps) {
    const { cart } = useCartStore()
    const { dictionary } = useDictionary()
    const pathname = usePathname()

    const steps = [
        { value: 'cart', title: dictionary.ui.cart.title },
        { value: 'shipping', title: dictionary.ui.checkout.billingAddress },
        { value: 'payment', title: dictionary.ui.checkout.checkout },
    ]

    const getCurrentStep = () => {
        if (!pathname) return 'cart'
        if (pathname.includes('/payment')) return 'payment'
        if (pathname.includes('/shipping')) return 'shipping'
        if (pathname.includes('/cart')) return 'cart'
        return 'cart'
    }

    const [mounted, setMounted] = useState(false)
    useEffect(() => {
        const t = setTimeout(() => setMounted(true), 0)
        return () => clearTimeout(t)
    }, [])

    const currentStep = getCurrentStep()

    if (mounted && (!cart?.items || cart.items.length === 0)) {
        return (
            <Container className="mt-6">
                <CartStepper steps={steps} currentStep={currentStep} />
                <div className="text-center py-12 space-y-6">
                    <svg className="mx-auto h-20 w-20 text-primary-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                            d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.5 6M7 13l-1.5 6m0 0h9m-9 0V19a2 2 0 002 2h7a2 2 0 002-2v-.5" />
                    </svg>
                    <Heading level={2}>{dictionary.ui.cart.emptyCart}</Heading>
                </div>
            </Container>
        )
    }

    return (
        <Container className="mt-6">
            <CartStepper steps={steps} currentStep={currentStep} />

            {(!mounted) ? (
                <CartSkeleton step={currentStep as 'cart' | 'shipping' | 'payment'} />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6">
                    <div className="lg:col-span-2">{children}</div>
                    <div className="lg:col-span-1">
                        <div className="sticky top-18">
                            <OrderSummary items={cart?.items ?? []} currentStep={currentStep} />
                        </div>
                    </div>
                </div>
            )}
        </Container>
    )
}